import * as Sentry from "@sentry/react";
import { isMobileDevice } from "@/lib/utils";
import { BlendedAudioAdapter } from "../infrastructure/adapters/BlendedAudioAdapter";
import { WebAudioAdapter } from "../infrastructure/adapters/WebAudioAdapter";
import {
	type IAudioRepository,
	AudioRepository,
} from "../infrastructure/AudioRepository";
import type { IApiClient } from "@/lib/__shared__/apiClient";
import type { AppManager } from "./AppManager";
import type {
	AudioPlaybackService,
	BlendedAudioPlaybackService,
} from "./ports";
import type { AppStore } from "./stores/appStore";
import { AdapterRegistry } from "./stores/AudioAdapterRegistry";
import {
	playbackStore,
	type PlaybackStoreProps,
	usePlaybackStore,
	type PlaybackStore,
} from "./stores/playbackStore";
import { StreamingAudioContextAdapter } from "../infrastructure/adapters/StreamingAudioContextAdapter";
import { AudioContextAdapter } from "../infrastructure/adapters/AudioContextAdapter";
import type { ResolvedTrackModel, TrackModel } from "@coffitivity/shared";
import { AUDIO_TYPE } from "@coffitivity/shared";
import {
	type ILocalRepository,
	LocalRepository,
} from "../infrastructure/LocalRepository";
import { timerStore } from "./stores/timerStore";

// Timer ID constants (should match AppManager)
const DAILY_LIMIT_TIMER_ID = "DAILY_LIMIT_TIMER";
const SIGNUP_TIMER_ID = "SIGNUP_TIMER";

export interface AudioManagerArgs {
	appStore: AppStore;
	showModalHandler: AppManager["showModal"];
	apiClient: IApiClient;
}

export interface IAudioManager {
	audioRepository: IAudioRepository;
	stateStore: PlaybackStore;
	deleteCustomTrack: (trackId: string) => Promise<void>;
	// addSubTrack(track: TrackModel): Promise<void>;
	// createNewBlend(userId: string): ResolvedTrackModel;
	getOrCreateAudioAdapter(
		track: ResolvedTrackModel | TrackModel,
	): Promise<AudioPlaybackService>;
	masterVolume(): number;
	// patchCurrentTrackData(track: ResolvedTrackModel | TrackModel): void;
	pauseTrack(baseTrackId?: string, blendTrackId?: string): Promise<void>;
	playTrack(
		baseTrack?: ResolvedTrackModel,
		blendTrackId?: string,
	): Promise<void>;
	addTrackToBlend(
		baseTrackId: string,
		blendTrack: ResolvedTrackModel,
	): Promise<void>;
	removeTrackFromBlend(
		baseTrackId: string,
		blendTrackId: string,
	): Promise<void>;
	setBlendTrackVolume(
		baseTrackId: string,
		blendTrackId: string,
		volume: number,
	): Promise<void>;
	// removeSubTrack(trackId: string): Promise<void>;
	setMasterVolume(volume: number): void;
	setIsCurrentTrackDraft(isDraft: boolean): void;
	promoteDraftTrack(oldTrackId: string, savedTrack: ResolvedTrackModel): void;
	stopAndCleanupTrack(trackId: string): Promise<void>;
	// setSubTrackVolume(trackId: string, volume: number): void;
	toggleMute(): void;
	updateTrackDetails(
		trackId: string,
		updates: Partial<Pick<ResolvedTrackModel, "name" | "description">>,
	): void;
	useStore: () => PlaybackStoreProps;
}

export class AudioManager implements IAudioManager {
	private static instance: AudioManager | null = null;
	private adapterRegistry: typeof AdapterRegistry;
	private showModal: AppManager["showModal"];
	private appStore: AppStore;
	private isMobile = false;
	public readonly MAX_BLEND_SUB_TRACKS = 6; // this is a constant that defines the maximum number of sub tracks allowed in a blend track

	audioRepository: IAudioRepository;
	localRepository: ILocalRepository = LocalRepository;
	stateStore: PlaybackStore;

	useStore: () => PlaybackStoreProps;

	private constructor(_args: AudioManagerArgs) {
		this.stateStore = playbackStore;
		this.useStore = usePlaybackStore;
		this.adapterRegistry = AdapterRegistry;
		this.appStore = _args.appStore;
		this.showModal = _args.showModalHandler;
		this.isMobile = isMobileDevice();
		this.audioRepository = new AudioRepository(_args.apiClient);

		// add a custom listener to the playbackStore to update the volume when it changes
		this.stateStore.subscribe(
			(state) => state.masterVolume,
			(newMasterVolume) => {
				AudioContextAdapter.getInstance().setMasterVolume(newMasterVolume);
			},
		);
	}

	/**
	 * Singleton access to the instance of the AudioManager.
	 */
	static getInstance(_args: AudioManagerArgs): AudioManager {
		if (!AudioManager.instance) {
			AudioManager.instance = new AudioManager(_args);
		}
		return AudioManager.instance;
	}

	async deleteCustomTrack(trackId: string): Promise<void> {
		try {
			await this.stopAndCleanupTrack(trackId);
			await this.audioRepository.deleteCustomBlend(trackId, {});

			return Promise.resolve();
		} catch (error) {
			Sentry.captureException(error);
			console.error("Error deleting custom track:", error);
			return Promise.reject(error);
		}
	}

	/**
	 * Returns an audio adapter by track ID. This method expose direct access to the
	 * playback controls to the app.
	 *
	 * @param track - The track to get the adapter for.
	 * @returns {Promise<AudioPlaybackService>} The audio adapter for the track.
	 */
	async getOrCreateAudioAdapter(
		track: ResolvedTrackModel | TrackModel,
	): Promise<AudioPlaybackService> {
		if (this.adapterRegistry.get(track.id)) {
			return this.adapterRegistry.get(track.id)!;
		}

		let adapter: AudioPlaybackService;
		if (track.type === AUDIO_TYPE.BLEND) {
			adapter = new BlendedAudioAdapter({
				store: this.stateStore,
				masterVolume: this.stateStore.getState().masterVolume,
				preferredMimeType: this.stateStore.getState().preferredMimeType,
				track: track as ResolvedTrackModel,
			});
		} else {
			if (this.isMobile) {
				adapter = new WebAudioAdapter({
					store: this.stateStore,
					track,
					masterVolume: this.stateStore.getState().masterVolume,
					preferredMimeType: this.stateStore.getState().preferredMimeType,
				});
			} else {
				adapter = new StreamingAudioContextAdapter({
					store: this.stateStore,
					track,
					masterVolume: this.stateStore.getState().masterVolume,
					preferredMimeType: this.stateStore.getState().preferredMimeType,
				});
			}
		}

		await adapter.load();

		this.adapterRegistry.register(track.id, adapter);

		return adapter;
	}

	masterVolume(): number {
		return this.stateStore.getState().masterVolume;
	}

	// Pause the currently playing track
	async pauseTrack(baseTrackId?: string, blendTrackId?: string): Promise<void> {
		const currentStatus = this.stateStore.getState().status;

		// if the status is already paused, return
		if (currentStatus === "PAUSED") return;

		if (baseTrackId) {
			const adapter = this.adapterRegistry.get(baseTrackId);
			if (adapter) {
				await adapter.pause(blendTrackId);
			}
		} else {
			const currentTrack = this.stateStore.getState().currentTrack;

			if (!currentTrack) {
				return;
			}

			const adapter = await this.getOrCreateAudioAdapter(currentTrack);
			await adapter.pause();
		}
	}

	async playTrack(
		_baseTrack?: ResolvedTrackModel,
		_blendTrackId?: string,
		_override = false,
	): Promise<void> {
		const currentTrack = this.stateStore.getState().currentTrack;
		const isSubscribed = this.appStore.getState().isUserSubscribed;
		const subscription = this.appStore.getState().subscription;
		const isLoggedIn = this.appStore.getState().session !== null;

		// DAILY LIMIT CHECK ------
		if (!isSubscribed) {
			const timer = timerStore.getState();

			// For logged-in users, check daily limit timer
			if (isLoggedIn) {
				const dailyLimitTimer = timer.timers[DAILY_LIMIT_TIMER_ID];
				if (dailyLimitTimer && dailyLimitTimer.remainingTime <= 0) {
					this.showModal("EXPIRED_LISTENING_MODAL", true);
					return;
				}
			} else {
				// For non-logged-in users, check signup timer
				const signupTimer = timer.timers[SIGNUP_TIMER_ID];
				if (signupTimer && signupTimer.remainingTime <= 0) {
					this.showModal("SIGNUP_MODAL", true);
					return;
				}
			}
		}

		// PREMIUM CHECK ----------
		// Check if the track requires an upgrade:
		// - For Plus tier users: block premium tracks only
		// - For non-subscribed users: block all premium tracks
		const isPlusTier = subscription?.planTier === "PLUS";
		const trackRequiresUpgrade =
			_baseTrack?.isPremium &&
			(!isSubscribed || (isPlusTier && _baseTrack.isPremium));

		if (trackRequiresUpgrade) {
			// show the upgrade modal
			this.showModal("UPGRADE_MODAL", true);
			// don't play the track
			return;
		}

		// pause the currently playing track if it's not the same as the new one
		if (
			currentTrack?.id !== _baseTrack?.id &&
			this.stateStore.getState().status === "PLAYING"
		) {
			this.pauseTrack(currentTrack?.id);
		}

		// try to get the new track if one was passed in
		if (_baseTrack) {
			const existingTrack = this.stateStore.getState().currentTrack;
			if (existingTrack?.id !== _baseTrack.id) {
				// Clear draft mode only when switching from one track to a different track
				// this prevents the audio player from being set in draft mode if the user navigated
				// before saving when creating a new blend
				if (existingTrack) {
					this.stateStore.getState().setIsCurrentTrackDraft(false);
				}
				this.stateStore.getState().setCurrentTrack(_baseTrack);
			}

			if (this.stateStore.getState().status !== "PLAYING") {
				this.stateStore.getState().setCurrentTrackStatus("LOADING");
			}

			try {
				const track = await this.getOrCreateAudioAdapter(_baseTrack);

				if (_blendTrackId) {
					await track.play(_blendTrackId);
					return;
				}

				await track.play(undefined, _override);
				return;
			} catch (error) {
				this.stateStore.getState().setCurrentTrackStatus("ERROR");
				throw error;
			}
		}

		// if no track was passed in and there is no current track, return
		// should never happen
		if (!currentTrack) {
			return;
		}

		// find the current track in the registry and play it
		if (this.stateStore.getState().status !== "PLAYING") {
			this.stateStore.getState().setCurrentTrackStatus("LOADING");
		}

		try {
			const adapter = await this.getOrCreateAudioAdapter(currentTrack);
			await adapter.play();
		} catch (error) {
			this.stateStore.getState().setCurrentTrackStatus("ERROR");
			throw error;
		}
	}

	async addTrackToBlend(
		baseTrackId: string,
		blendTrack: ResolvedTrackModel,
	): Promise<void> {
		const adapter = this.adapterRegistry.get(baseTrackId);
		if (!adapter || adapter.data.type !== AUDIO_TYPE.BLEND) {
			return;
		}

		const blendAdapter = adapter as BlendedAudioPlaybackService;
		const currentBlendItems = blendAdapter.data.blend ?? [];

		if (currentBlendItems.length >= this.MAX_BLEND_SUB_TRACKS) {
			return;
		}

		if (currentBlendItems.some((item) => item.track.id === blendTrack.id)) {
			return;
		}

		await blendAdapter.addBlendTrack(blendTrack);
		this.stateStore
			.getState()
			.updateCurrentBlend(blendAdapter.data.id, blendAdapter.data.blend ?? []);
	}

	async removeTrackFromBlend(
		baseTrackId: string,
		blendTrackId: string,
	): Promise<void> {
		const adapter = this.adapterRegistry.get(baseTrackId);
		if (!adapter || adapter.data.type !== AUDIO_TYPE.BLEND) {
			return;
		}

		const blendAdapter = adapter as BlendedAudioPlaybackService;
		const currentBlendItems = blendAdapter.data.blend ?? [];
		if (!currentBlendItems.some((item) => item.track.id === blendTrackId)) {
			return;
		}

		await blendAdapter.removeBlendTrack(blendTrackId);
		this.stateStore
			.getState()
			.updateCurrentBlend(blendAdapter.data.id, blendAdapter.data.blend ?? []);
	}

	async setBlendTrackVolume(
		baseTrackId: string,
		blendTrackId: string,
		volume: number,
	): Promise<void> {
		const adapter = this.adapterRegistry.get(baseTrackId);
		if (!adapter || adapter.data.type !== AUDIO_TYPE.BLEND) {
			return;
		}

		const blendTrackStatus = adapter
			.status()
			.blendStatus.find((sub) => sub.trackId === blendTrackId);

		if (blendTrackStatus?.status !== "PLAYING") {
			await adapter.play(blendTrackId);
		}

		adapter.setTrackVolume(volume, blendTrackId);
	}

	setMasterVolume(volume: number): void {
		this.stateStore.getState().setVolume(volume);
	}

	setIsCurrentTrackDraft(isDraft: boolean): void {
		this.stateStore.getState().setIsCurrentTrackDraft(isDraft);
	}

	/**
	 * Promotes a draft track to a saved track by updating the adapter with the
	 * server-returned data (new id, slug, etc.) and re-registering it.
	 */
	promoteDraftTrack(oldTrackId: string, savedTrack: ResolvedTrackModel): void {
		const adapter = this.adapterRegistry.get(oldTrackId);
		if (!adapter) {
			return;
		}

		// Remove from registry without cleanup (we want to keep audio playing)
		this.adapterRegistry.remove(oldTrackId);

		// Update the adapter's data with all new values from saved track
		adapter.data = {
			...adapter.data,
			...savedTrack,
		} as typeof adapter.data;

		// Re-register with the new ID
		this.adapterRegistry.register(savedTrack.id, adapter);

		// Update current track in the playback store
		const currentTrack = this.stateStore.getState().currentTrack;
		if (currentTrack?.id === oldTrackId) {
			this.stateStore.getState().setCurrentTrack({
				...currentTrack,
				...savedTrack,
			});
		}
	}

	/**
	 * Stops playback and fully cleans up an adapter (removes from registry and releases resources).
	 */
	async stopAndCleanupTrack(trackId: string): Promise<void> {
		await this.pauseTrack(trackId);
		this.adapterRegistry.unregister(trackId);

		// Clear current track if it matches
		const currentTrack = this.stateStore.getState().currentTrack;
		if (currentTrack?.id === trackId) {
			this.stateStore.getState().clearCurrentTrack();
		}
	}

	toggleMute(): void {
		this.stateStore.getState().toggleMute();
	}

	updateTrackDetails(
		trackId: string,
		updates: Partial<Pick<ResolvedTrackModel, "name" | "description">>,
	): void {
		const adapter = this.adapterRegistry.get(trackId);

		if (adapter) {
			adapter.data = {
				...adapter.data,
				...updates,
			} as typeof adapter.data;
		}

		const currentTrack = this.stateStore.getState().currentTrack;
		if (currentTrack?.id === trackId) {
			this.stateStore.getState().setCurrentTrack({
				...currentTrack,
				...updates,
			});
		}
	}

	// async unlock(): Promise<void> {
	// 	if (this.isUnlocked) return;
	// 	if (this.audioContext.state === "suspended") {
	// 		await this.audioContext.resume();
	// 	}
	// 	this.isUnlocked = true;
	// }
}

export type AudioManagerType = AudioManager;
